import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronsLeft, ChevronsRight,
  LayoutDashboard, UsersRound, Ticket,
  Trophy, WalletCards, ShieldCheck, UserRound, LogOut,
  CircleDollarSign,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const primaryNav = [
  { icon: LayoutDashboard, label: "Overview", to: "/dashboard", end: true },
];

const navGroups = [
  {
    id: "play",
    label: "Play",
    icon: Ticket,
    items: [
      { icon: UsersRound, label: "My Pool", to: "/dashboard/pool" },
      { icon: Ticket, label: "Tickets", to: "/dashboard/tickets" },
      { icon: Trophy, label: "Results", to: "/dashboard/results" },
    ],
  },
  {
    id: "account",
    label: "Account",
    icon: CircleDollarSign,
    items: [
      { icon: WalletCards, label: "Payments", to: "/dashboard/payments" },
      { icon: ShieldCheck, label: "Subscription", to: "/dashboard/subscription" },
      { icon: UserRound, label: "Profile", to: "/dashboard/profile" },
    ],
  },
];

export default function UserSideNav() {
  const [collapsed, setCollapsed] = React.useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initials = user?.name?.charAt(0)?.toUpperCase() || "U";
  const logoUrl = `${process.env.PUBLIC_URL}/logo.png`;
  const activeGroup = navGroups.find((group) =>
    group.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))
  )?.id;

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <aside className={`user-side-nav ${collapsed ? "collapsed" : ""}`} aria-label="User dashboard navigation">
      <div className="user-side-brand">
        <img src={logoUrl} alt="Lucky Pool" />
        <div>
          <strong>Lucky Pool</strong>
          <span>Powerball pools</span>
        </div>
        <button
          className="user-side-collapse"
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      <nav className="user-side-menu">
        <div className="user-nav-section">
          {primaryNav.map(({ icon: Icon, label, to, end }) => (
            <NavLink
              key={label}
              to={to}
              end={end}
              className={({ isActive }) => isActive ? "active user-nav-link" : "user-nav-link"}
              aria-label={label}
            >
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>

        {navGroups.map((group) => {
          const isActiveGroup = activeGroup === group.id;

          return (
            <div className={`user-nav-section ${isActiveGroup ? "active-group" : ""}`} key={group.id}>
              <div className="user-nav-section-title">{group.label}</div>
              <div className="user-nav-list" aria-label={`${group.label} navigation`}>
                {group.items.map(({ icon: Icon, label, to }) => (
                  <NavLink
                    key={label}
                    to={to}
                    className={({ isActive }) => isActive ? "active user-nav-link" : "user-nav-link"}
                    aria-label={label}
                  >
                    <Icon size={19} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="user-side-bottom">
        <div className="user-side-account">
          <span className="avatar small">{initials}</span>
          <div>
            <strong>{user?.name || "User"}</strong>
            <span>{user?.email || "Member account"}</span>
          </div>
        </div>
        <button
          className="user-nav-link user-logout"
          onClick={handleLogout}
          aria-label="Logout"
        >
          <LogOut size={19} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
